import {
  createStyles,
  Grid,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type Theme,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { Trans, useTranslations } from "adapters/i18n";
import { BannerAlert } from "features/shared/components";
import React from "react";
import type { ProjectedWeight } from "shared/types";
import { parseProjectionToTableData } from "../utils/lots.utils";

type WeightProjectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  weightGainProjection: ProjectedWeight | null;
} & WithStyles<typeof styles>;

function WeightProjectionModal({
  classes,
  isOpen,
  onClose,
  weightGainProjection,
}: WeightProjectionModalProps) {
  const { t } = useTranslations();

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      data-testid="weight-projection-modal"
      onBackdropClick={handleClose}
      onClose={handleClose}
      open={isOpen}
    >
      <div className={classes.root}>
        <Grid
          className={classes.content}
          container={true}
          direction="row"
          justify="space-evenly"
          spacing={0}
        >
          <Grid item xs={10}>
            <Typography className={classes.header} variant="h6">
              {t("livestock:weight-gain-projection")}
            </Typography>
          </Grid>

          <Grid item xs={2}>
            <CloseIcon
              className={classes.closeButton}
              data-testid="close-modal-button"
              onClick={handleClose}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography align="left" className={classes.subHeader} variant="h6">
              {t("livestock:check-total-weight-gain-projection")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <BannerAlert className={classes.disclaimer} type="info">
              <span>
                <Trans
                  i18nKey="livestock:weight-gain-projection-disclaimer"
                  components={{ bold: <strong /> }}
                />
              </span>
            </BannerAlert>
          </Grid>

          <Grid item className={classes.tableContainer}>
            <WeightProjectionTable
              classes={classes}
              projection={weightGainProjection}
            />
          </Grid>
        </Grid>
      </div>
    </Modal>
  );
}

type WeightProjectionTableProps = {
  projection: ProjectedWeight | null;
} & WithStyles<typeof styles>;

function WeightProjectionTable({
  classes,
  projection,
}: WeightProjectionTableProps) {
  const { currentLanguage, t } = useTranslations();

  const tableData = parseProjectionToTableData(
    projection,
    currentLanguage.locale
  );

  return (
    <Table className={classes.table} data-testid="weight-projection-table">
      <TableHead>
        <TableRow className={classes.tableHeaderRow}>
          <TableCell
            align="left"
            className={classes.tableHeaderCell}
            style={{ padding: "4px 30px 4px 24px", width: "140px" }}
          >
            {t("common:fields.date")}
          </TableCell>
          <TableCell
            align="left"
            className={classes.tableHeaderCell}
            style={{ padding: "4px 10px 4px 20px", width: "200px" }}
          >
            {t("livestock:expected-weight-kg")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {tableData.map((row) => (
          <TableRow className={classes.tableBodyRow} key={row.date}>
            <TableCell
              align="left"
              className={classes.tableRowCell}
              style={{
                backgroundColor: "#fafafa",
                padding: "4px 30px 4px 24px",
              }}
            >
              {row.date}
            </TableCell>
            <TableCell
              align="left"
              className={classes.tableRowCell}
              style={{ fontWeight: "bold", padding: "4px 10px 4px 20px" }}
            >
              {row.expectedWeight}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      alignItems: "center",
      display: "flex",
      flex: 1,
      height: "100%",
      justifyContent: "center",
    },
    content: {
      backgroundColor: theme.palette.common.white,
      borderRadius: "16px",
      display: "flex",
      flexDirection: "row",
      height: "fit-content",
      justifyContent: "space-evenly",
      padding: "24px 40px",
      rowGap: "16px",
      width: "min-content",
    },
    header: {
      color: "rgba(51, 50, 56, 0.84)",
      fontSize: "18px",
      fontStretch: "normal",
      fontStyle: "normal",
      fontWeight: 500,
      letterSpacing: "-0.1px",
      lineHeight: "normal",
    },
    closeButton: {
      cursor: "pointer",
      height: "24px",
      left: "100%",
      objectFit: "contain",
      padding: "0",
      position: "relative",
      top: "0",
      width: "24px",
    },
    subHeader: {
      color: "#8e8e93",
      fontSize: "14px",
      fontStretch: "normaL",
      fontStyle: "normal",
      fontWeight: "normal",
      letterSpacing: "0px",
      lineHeight: 1.43,
      textAlign: "left",
    },
    disclaimer: {
      height: "fit-content",
      width: "538px",
    },
    tableContainer: {
      borderRadius: "3px",
      height: "299.4px",
      padding: "0",
      position: "relative",
      width: "539px",
    },
    table: {
      border: "solid 0.5px #d1d1d6",
      borderRadius: "3px",
      color: "#666666",
      fontFamily: "Roboto",
      fontSize: "14px",
      height: "299.4px",
      letterSpacing: "-0.03px",
      margin: "0 0",
      padding: "0",
      width: "538px",
    },
    tableHeaderRow: {
      height: "44px",
      maxHeight: "44px",
    },
    tableBodyRow: {
      height: "40px",
      maxHeight: "44px",
    },
    tableHeaderCell: {
      backgroundColor: "#e1e1e1",
      border: "solid 0.5px #e1e1e1",
      color: theme.palette.primary.main,
      fontFamily: "Roboto",
      fontSize: 14,
      height: "auto",
      textTransform: "uppercase",
    },
    tableRowCell: {
      border: "solid 0.5px #e5e5ea",
      color: "#666666",
      fontFamily: "Roboto",
      fontSize: "14px",
      fontStretch: "normal",
      fontStyle: "normal",
      height: "auto",
      letterSpacing: "normal",
      lineHeight: "normal",
    },
  });

export default withStyles(styles)(WeightProjectionModal);
